<template>
  <div class="share-reading-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('shareReading.list.title')" :subtitle="$t('shareReading.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="_openAddShareReadingModal">
          {{ $t('shareReading.list.addReading') }}
        </el-button>
      </template>
    </ds-page-header>

    <div class="share-reading-layout">
      <!-- 左侧状态筛选 -->
      <div class="ds-panel ds-panel--tree">
        <ul class="state-list">
          <li v-for="(item, index) in shareReadingInfo.states" :key="index" @click="swatchShareState(item)"
            :class="{ 'active-state': shareReadingInfo.conditions.state === item.statusCd }">
            {{ item.name }}
          </li>
        </ul>
      </div>

      <div class="share-reading-main">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="shareReadingInfo.conditions.fsmId" clearable class="w-full"
                :placeholder="$t('shareReading.search.selectMeter')">
                <el-option v-for="(item, index) in shareReadingInfo.floorShares" :key="index"
                  :label="`${item.floorNum}栋-${item.meterTypeName}`" :value="item.fsmId" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="shareReadingInfo.conditions.startTime" type="datetime" class="w-full"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('shareReading.search.startTime')" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="shareReadingInfo.conditions.endTime" type="datetime" class="w-full"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('shareReading.search.endTime')" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="_queryShareReadingMethod">
              {{ $t('common.search') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="_listShareReadings">
            <template #left>
              <span class="ds-panel__title">{{ $t('shareReading.list.title') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="shareReadingInfo.shareReadings" :loading="loading" :error="hasError"
            :error-message="errorMessage" :density="density" border :empty-description="$t('shareReading.page.empty')"
            @retry="_listShareReadings">
            <el-table-column prop="readingId" :label="$t('shareReading.table.id')" align="center" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.readingId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="title" :label="$t('shareReading.table.name')" align="left" min-width="140"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.title || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="preDegrees" :label="$t('shareReading.table.preDegrees')" align="right" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.preDegrees == null ? '-' : scope.row.preDegrees }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="curDegrees" :label="$t('shareReading.table.curDegrees')" align="right" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.curDegrees == null ? '-' : scope.row.curDegrees }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('shareReading.table.degrees')" align="right" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ getDegrees(scope.row) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="preReadingTime" :label="$t('shareReading.table.preReadingTime')" align="center"
              width="170">
              <template slot-scope="scope">
                {{ formatDateTimeSec(scope.row.preReadingTime) }}
              </template>
            </el-table-column>
            <el-table-column prop="curReadingTime" :label="$t('shareReading.table.curReadingTime')" align="center"
              width="170">
              <template slot-scope="scope">
                {{ formatDateTimeSec(scope.row.curReadingTime) }}
              </template>
            </el-table-column>
            <el-table-column prop="createStaffName" :label="$t('shareReading.table.reader')" align="center" width="100">
              <template slot-scope="scope">
                {{ scope.row.createStaffName || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('shareReading.table.status')" align="center" width="110">
              <template slot-scope="scope">
                <ds-status-tag :type="resolveStateType(scope.row.state)" :label="resolveStateLabel(scope.row.state)" />
              </template>
            </el-table-column>
            <el-table-column prop="remark" :label="$t('shareReading.table.remark')" align="left" min-width="140"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.remark || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="auditStaffName" :label="$t('shareReading.table.auditor')" align="center" width="100">
              <template slot-scope="scope">
                {{ scope.row.auditStaffName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="stateMsg" :label="$t('shareReading.table.auditOpinion')" align="left" min-width="140"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.stateMsg || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="shareMsg" :label="$t('shareReading.table.shareProgress')" align="left" min-width="140"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.shareMsg || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="createTime" :label="$t('shareReading.table.readingTime')" align="center" width="170">
              <template slot-scope="scope">
                {{ formatDateTimeSec(scope.row.createTime) }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" align="center" width="190">
              <template slot-scope="scope">
                <el-button v-if="scope.row.state === 'W' && hasPrivilege('502025032678040006')" type="text" size="small"
                  @click="_openAuditShareReadingModel(scope.row)">
                  {{ $t('common.audit') }}
                </el-button>
                <el-button v-if="scope.row.state === 'W'" type="text" size="small" class="ds-btn-danger"
                  @click="_openDeleteShareReadingModel(scope.row)">
                  {{ $t('common.delete') }}
                </el-button>
                <el-button type="text" size="small" @click="_openShareFee(scope.row)">
                  {{ $t('shareReading.table.shareFee') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </div>
    </div>

    <add-share-reading ref="addShareReading" @success="handleSuccess" />
    <audit-share-reading ref="auditShareReading" @success="handleSuccess" />
    <delete-share-reading ref="deleteShareReading" @success="handleSuccess" />
  </div>
</template>

<script>
import { getDict, getCommunityId } from '@/api/community/communityApi'
import { listFloorShareReading, listFloorShareMeter } from '@/api/fee/shareReadingApi'
import AddShareReading from '@/components/fee/addShareReading'
import AuditShareReading from '@/components/fee/auditShareReading'
import DeleteShareReading from '@/components/fee/deleteShareReading'
import { formatDateTimeSec } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ShareReadingList',
  components: {
    AddShareReading,
    AuditShareReading,
    DeleteShareReading,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      shareReadingInfo: {
        shareReadings: [],
        states: [],
        floorShares: [],
        conditions: {
          fsmId: '',
          state: '',
          communityId: '',
          startTime: '',
          endTime: '',
          page: 1,
          row: 10
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.initData()
  },
  methods: {
    async initData() {
      try {
        this.loading = true
        this.shareReadingInfo.conditions.communityId = getCommunityId()

        // 获取状态字典
        const states = await getDict('floor_share_reading', 'state')
        this.shareReadingInfo.states = [{ statusCd: '', name: this.$t('common.all') }, ...states]

        await this._listFloorShares()
        await this._listShareReadings()
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('shareReading.page.loadFailed')
        console.error('初始化数据失败:', error)
      } finally {
        this.loading = false
      }
    },
    async _listShareReadings() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.shareReadingInfo.conditions,
          page: this.page.current,
          row: this.page.size
        }
        const { data, total } = await listFloorShareReading(params)
        this.shareReadingInfo.shareReadings = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('shareReading.page.loadFailed')
        console.error('获取公摊抄表列表失败:', error)
      } finally {
        this.loading = false
      }
    },
    async _listFloorShares() {
      try {
        const params = {
          page: 1,
          row: 300,
          communityId: getCommunityId()
        }
        const { data } = await listFloorShareMeter(params)
        this.shareReadingInfo.floorShares = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('获取公摊表列表失败:', error)
      }
    },
    _openAddShareReadingModal() {
      this.$refs.addShareReading.open()
    },
    _openAuditShareReadingModel(row) {
      this.$refs.auditShareReading.open(row)
    },
    _openDeleteShareReadingModel(row) {
      this.$refs.deleteShareReading.open(row)
    },
    _queryShareReadingMethod() {
      this.page.current = 1
      this._listShareReadings()
    },
    swatchShareState(state) {
      this.shareReadingInfo.conditions.state = state.statusCd
      this._queryShareReadingMethod()
    },
    _openShareFee(reading) {
      this.$router.push(`/views/fee/shareReadingFee?readingId=${reading.readingId}`)
    },
    handleSuccess() {
      this._listShareReadings()
    },
    handleSizeChange(val) {
      this.page.size = val
      this._listShareReadings()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listShareReadings()
    },
    getDegrees(row) {
      if (row.preDegrees == null || row.curDegrees == null) return '-'
      return row.curDegrees - row.preDegrees
    },
    resolveStateType(state) {
      if (state === 'C') return 'success'
      if (state === 'W') return 'warning'
      return 'danger'
    },
    resolveStateLabel(state) {
      if (state === 'W') return this.$t('shareReading.status.waiting')
      if (state === 'C') return this.$t('shareReading.status.passed')
      return this.$t('shareReading.status.rejected')
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.share-reading-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧状态筛选 + 右侧主内容 */
.share-reading-layout {
  display: flex;
  gap: var(--ds-spacing-md);
  align-items: flex-start;
}

.share-reading-main {
  flex: 1 1 auto;
  min-width: 0;
}

/* 状态筛选面板 */
.ds-panel--tree {
  width: 200px;
  flex-shrink: 0;
}

.state-list {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    padding: 10px;
    text-align: center;
    cursor: pointer;
    border-radius: var(--ds-radius-sm);
    margin-bottom: 5px;
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-700);
    transition: background-color var(--ds-transition-fast), color var(--ds-transition-fast);

    &:hover {
      background-color: var(--ds-gray-100);
    }

    &.active-state {
      background-color: var(--ds-primary);
      color: #fff;
    }
  }
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}
</style>
